const GOOGLE_ANALYTICS_ID = "G-TBWP49Q2H9"

const UUID_SEGMENT = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}"

const STATIC_ROUTES = new Set([
  "/",
  "/articles",
  "/bazi-calculator",
  "/bazi-career-crossroads",
  "/bazi-money-luck",
  "/bazi-relationship-dilemmas",
  "/dashboard",
  "/flying-star-calculator",
  "/flying-stars",
  "/how-it-works",
  "/login",
  "/login/code",
  "/privacy",
  "/register",
  "/sample-report",
  "/settings",
  "/terms",
  "/yijing-divination",
])

const DYNAMIC_ROUTES: [RegExp, string][] = [
  [new RegExp(`^/charts/${UUID_SEGMENT}$`), "/charts/:chart_id"],
  [new RegExp(`^/charts/${UUID_SEGMENT}/messages/${UUID_SEGMENT}$`), "/charts/:chart_id/messages/:message_id"],
  // One stable rule for every published forecast detail page (annual and
  // monthly), so future solar-term slugs classify instead of /other.
  [/^\/flying-stars\/[^/]+$/, "/flying-stars/:slug"],
]

export type ConsultationPlanKey = "chart_reading_01" | "chart_follow_up_01"
export type PassCheckoutContext = "chart" | "dashboard_pass"
export type FlyingStarCtaSource = "index" | "annual_forecast" | "monthly_forecast" | "calculator"
export type FreeToolCtaSource = "bazi_calculator" | "yijing_divination" | "flying_star_calculator"
// Page rendering the free tools band: a tool page, or a content page that shows all three tools.
export type FreeToolsCtaSource = FreeToolCtaSource | "article" | "annual_forecast" | "monthly_forecast"

// ── Frozen funnel event catalog (C10, #EVT) ──────────────────────────────────
// Baseline-sensitive: do not rename, repurpose, or drop these names without a
// product decision. #EVT freezes event name, trigger moment, required
// properties, and property meaning before baseline collection starts.
//
// Trigger semantics:
//   server-confirmed — fired only after the server reports the underlying
//     action succeeded; never optimistic.
//   attempt metric — fired on a user action or observed state, even if a
//     later server step rejects it.
//
// New-catalog trigger semantics:
//   chart_generated — server-confirmed successful calculation render
//     (replaces bazi_calculator_chart_generated). visitor_type (added
//     2026-10-29, owner request) splits signed-in from guest use.
//   save_snapshot_cta_clicked — attempt metric, fired once on the calculator
//     CTA into register (ownership classification retired with the subject
//     policy — decision B).
//   free_snapshot_started — attempt metric, fired on the generate button; the
//     button is the only generation trigger (AC5/decision G), so the former
//     "auto" trigger dimension is retired (replaces teaser_started).
//   free_snapshot_completed — fired once when the completed snapshot becomes
//     visible (replaces teaser_completed).
//   paid_offer_clicked — attempt metric on checkout-form submit of the
//     locked-reading card; plan_key identifies the offer.
//   complete_reading_started — server-confirmed initial-analysis submission
//     success.
//   complete_reading_completed — fired once per chart (per-mount +
//     per-chartId guard) when an initial-analysis assistant message this
//     mount observed while still pending later becomes completed; messages
//     already complete at mount never fire.
//   first_question_asked — server-confirmed follow-up submit success when no
//     prior follow-up assistant response exists (the initial analysis's
//     synthetic user message does not count as a question).
//   topic_created — Retired (decision A): the topics/threads layer is
//     removed and the event no longer fires.
//
// Privacy boundary: funnel events carry no emails, names, subject names,
// question text, or chart IDs — only the existing GA page context and the
// documented properties below.
//
// Property naming: never send an event parameter called `source`, `medium`,
// `campaign`, `term` or `content`. GA4 reads those names as traffic-source
// overrides, so a CTA click carrying `source: "hero"` rewrote the session's
// origin (it surfaced as "consultation_offer / (not set)" and "Organic
// Shopping" in Traffic acquisition, 2026-10-28). CTA placement is `cta_source`.
// Free tools tag whether a signed-in account or a guest used them.
export type VisitorType = "registered" | "guest"

export type FunnelEvent =
  // Activation funnel — frozen catalog
  | { name: "chart_generated"; visitor_type: VisitorType }
  | { name: "save_snapshot_cta_clicked" }
  | { name: "email_auth_started"; entry_intent: "signup" | "login" }
  | { name: "login_code_send_accepted" }
  | { name: "login_code_verified" }
  | { name: "account_created" }
  | { name: "chart_created" }
  | { name: "free_snapshot_started" }
  | { name: "free_snapshot_completed" }
  | { name: "paid_offer_clicked"; plan_key: ConsultationPlanKey }
  | { name: "checkout_started"; plan_key: ConsultationPlanKey; checkout_context: PassCheckoutContext }
  | { name: "complete_reading_started" }
  | { name: "complete_reading_completed" }
  | { name: "first_question_asked" }
  // Retired (decision A): topic_created no longer fires with the threads/topics
  // layer removed.
  // Retained view/entitlement metrics
  | { name: "how_it_works_viewed"; cta_source: "consultation_offer" }
  | { name: "consultation_offer_viewed"; plan_key: "chart_reading_01" }
  | { name: "teaser_panel_viewed" }
  | { name: "chart_grant_redeemed"; plan_key: ConsultationPlanKey }
  // Non-catalog funnel events, unchanged and still fired
  | { name: "returning_login_completed" }
  // Yi Jing tool: a server-computed reading rendered. topic is the intake
  // category key only, never the question or cast.
  | { name: "yijing_reading_viewed"; topic: string; visitor_type: VisitorType }
  | {
      name: "career_crossroads_cta_clicked"
      destination: "free_chart" | "registration"
      cta_source: "hero" | "question_prompt" | "walkthrough" | "pricing" | "final_cta"
    }
  | { name: "career_crossroads_guide_clicked"; guide_slug: string }
  | {
      name: "relationship_dilemmas_cta_clicked"
      destination: "free_chart" | "registration"
      cta_source: "hero" | "question_prompt" | "compatibility" | "walkthrough" | "pricing" | "final_cta"
    }
  | { name: "relationship_dilemmas_guide_clicked"; guide_slug: string }
  | {
      name: "money_luck_cta_clicked"
      destination: "free_chart" | "registration"
      cta_source: "hero" | "question_prompt" | "walkthrough" | "pricing" | "final_cta"
    }
  | { name: "money_luck_guide_clicked"; guide_slug: string }
  | {
      name: "home_cta_clicked"
      destination: "free_chart" | "registration"
      cta_source: "pricing"
    }
  | { name: "footer_cta_clicked" }
  | {
      name: "free_tools_cta_clicked"
      cta_source: FreeToolsCtaSource
      target: FreeToolCtaSource
    }

type PurchaseEvent = {
  transaction_id: string
  plan_key: ConsultationPlanKey
  checkout_context: PassCheckoutContext
  amount_cents: number
  currency: string
  item_name: string
}

export type FlyingStarEvent =
  | { name: "flying_star_property_calculated"; visitor_type: VisitorType }
  | { name: "flying_star_property_form_error" }
  | {
      name: "flying_star_category_selected"
      category: "career" | "money" | "love" | "studies" | "health" | "support"
      stance: "supportive" | "cautionary" | "no_clear_signal"
    }
  | { name: "flying_star_bazi_cta_clicked"; cta_source: FlyingStarCtaSource }

export function analyticsPagePath(url: string) {
  let pathname: string

  try {
    pathname = new URL(url, "https://superbazi.me").pathname
  } catch {
    return "/other"
  }

  if (STATIC_ROUTES.has(pathname)) return pathname

  // Public editorial slugs identify the content being read. Keep queries and
  // fragments stripped, while private record routes remain masked below.
  if (/^\/articles\/(?:tags\/)?[a-z0-9]+(?:-[a-z0-9]+)*$/.test(pathname)) return pathname

  for (const [pattern, route] of DYNAMIC_ROUTES) {
    if (pattern.test(pathname)) return route
  }

  // Retain unknown paths for diagnostics, but mask private identifiers.
  return pathname
    .replace(new RegExp(UUID_SEGMENT, "g"), ":id")
    .replace(/^\/passwords\/[^/]+\/edit$/, "/passwords/:token/edit")
}

// `lastTrackedLocation` dedupes page_view events against the raw navigation
// key (pathname + search + hash). `lastTrackedPageLocation` records the current
// page's sanitized GA4 page_location so the *next* page_view can report it as
// page_referrer. They track different things (the raw event vs. the prior
// sanitized page for referrer attribution) and cannot be collapsed without
// changing dedupe behavior, so both remain.
let lastTrackedLocation: string | null = null
let lastTrackedPageLocation: string | null = null

function analyticsContext(url = window.location.href) {
  const pagePath = analyticsPagePath(url)

  return {
    page_location: `${window.location.origin}${pagePath}`,
    page_path: pagePath,
    page_title: document.title,
  }
}

// Parses document.referrer once so sanitizedDocumentReferrer() and
// referrerContext() share a single source of truth instead of each re-parsing.
function documentReferrer(): { url: URL } | null {
  if (typeof document === "undefined" || !document.referrer) return null

  try {
    return { url: new URL(document.referrer) }
  } catch {
    return null
  }
}

// Sanitizes the document referrer for GA4: same-origin referrers are
// normalized to `${origin}${pagePath}`, cross-origin ones to `${origin}/`.
// Only page_view uses this; the other events report no page_referrer.
function sanitizedDocumentReferrer() {
  const parsed = documentReferrer()
  if (!parsed) return ""

  if (parsed.url.origin === window.location.origin) {
    return `${parsed.url.origin}${analyticsPagePath(parsed.url.href)}`
  }

  return `${parsed.url.origin}/`
}

// The single referrer flag spread across every tracking event: `ignore_referrer`
// is set when Stripe Checkout is the referrer, otherwise empty so no referrer
// keys are added to the payload.
function referrerContext(): { ignore_referrer?: true } {
  const parsed = documentReferrer()
  return parsed && parsed.url.hostname === "checkout.stripe.com" ? { ignore_referrer: true } : {}
}

// Set by the server for excluded (owner) accounts. The layout skips GA on
// the next full load; this stops sends in the current Inertia session too,
// including gtag's own Enhanced Measurement events via Google's kill switch.
export function analyticsOptedOut() {
  const optedOut = typeof document !== "undefined" && /(?:^|;\s*)analytics_opt_out=1(?:;|$)/.test(document.cookie ?? "")
  if (optedOut && typeof window !== "undefined") (window as unknown as Record<string, unknown>)[`ga-disable-${GOOGLE_ANALYTICS_ID}`] = true
  return optedOut
}

function track(eventName: string, params: object) {
  if (typeof window === "undefined" || typeof window.gtag !== "function" || analyticsOptedOut()) return
  window.gtag("event", eventName, params)
}

export function trackPageView(url: string) {
  if (typeof window.gtag === "function" && !analyticsOptedOut()) window.gtag("set", analyticsContext(url))
  const location = new URL(url, window.location.origin)
  const locationKey = `${location.pathname}${location.search}${location.hash}`
  if (locationKey === lastTrackedLocation) return

  lastTrackedLocation = locationKey
  const context = analyticsContext(url)
  track("page_view", {
    ...context,
    page_referrer: lastTrackedPageLocation ?? sanitizedDocumentReferrer(),
    ...referrerContext(),
    send_to: GOOGLE_ANALYTICS_ID,
  })
  lastTrackedPageLocation = context.page_location
}

// PostHog receives these browser-only events too, with their own fields and
// no GA page context. lib/posthog.ts registers the sender once it has started,
// so this file stays free of the posthog-js import.
const POSTHOG_MIRRORED_EVENTS = new Set([
  "chart_generated",
  "flying_star_property_calculated",
  "yijing_reading_viewed",
  "consultation_offer_viewed",
  "checkout_started",
])
let posthogMirror: ((name: string, fields: Record<string, unknown>) => void) | null = null

export function setPosthogMirror(sender: typeof posthogMirror) {
  posthogMirror = sender
}

function mirrorToPosthog(name: string, fields: Record<string, unknown>) {
  if (!posthogMirror || !POSTHOG_MIRRORED_EVENTS.has(name) || analyticsOptedOut()) return
  try {
    posthogMirror(name, fields)
  } catch {
    // PostHog must never stop the GA event or break the caller.
  }
}

export function trackFunnelEvent(event: FunnelEvent) {
  const { name, ...fields } = event
  mirrorToPosthog(name, fields)
  track(name, {
    ...fields,
    ...analyticsContext(),
    ...referrerContext(),
  })
}

export function trackFlyingStarEvent(event: FlyingStarEvent) {
  const { name, ...fields } = event
  mirrorToPosthog(name, fields)
  track(name, {
    ...fields,
    ...analyticsContext(),
    ...referrerContext(),
  })
}

export function trackPurchase(event: PurchaseEvent) {
  const { amount_cents, checkout_context, currency, item_name, plan_key, transaction_id } = event
  const value = amount_cents / 100

  track("purchase", {
    transaction_id,
    value,
    currency,
    checkout_context,
    plan_key,
    items: [ {
      item_id: plan_key,
      item_name,
      price: value,
      quantity: 1,
    } ],
    ...analyticsContext(),
    ...referrerContext(),
  })
}
